// src/shared-components/cards/components/ArtlistSliderCard.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PlayIcon from "src/assets/all-icons/play-icon.jsx";

import config from "@/config";
import fetchWrapper from "@/services/fetchWrapper.service";
import ArtworkImage from "@/shared-components/cards/components/ArtworkImage";
import Text from "@/shared-components/text/components/Text";

import "./styles/ArtlistSliderCard.css";

export default function ArtlistSliderCard({ artlist }) {
  // artlist.artlist_image.image_id
  // artlist.artlist_title, artlist.artlist_creator.user_username
  const [isFetchingArtlist, setIsFetchingArtlist] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const rawImage = artlist?.artlist_image;
  const imageId = typeof rawImage === "string" ? rawImage : rawImage?.image_id;
  const title = artlist?.artlist_title;
  const rawCreator = artlist?.artlist_creator;
  const author =
    typeof rawCreator === "string"
      ? rawCreator
      : rawCreator?.user_username ||
        rawCreator?.user_profile_info?.public_name ||
        rawCreator?.user_profile_info?.name ||
        rawCreator?.channel_name ||
        "";
  const artlistId = artlist?._id ?? artlist?.id;
  const navigate = useNavigate();

  const hasPlayableArtworks = Array.isArray(artlist?.artlist_artworks) && artlist.artlist_artworks.length > 0;

  function navigateToArtlistView() {
    if (!artlistId) {
      setFetchError("Missing artlist information to play this selection.");
      return;
    }

    navigate(`/artlist/${encodeURIComponent(artlistId)}`, {
      state: { openTimeSelector: true },
    });
  }

  async function handlePlayClick(event) {
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();
    event.preventDefault();

    if (isFetchingArtlist) {
      return;
    }

    if (!artlistId) {
      setFetchError("Missing artlist information to play this selection.");
      return;
    }

    if (hasPlayableArtworks) {
      setFetchError(null);
      navigateToArtlistView();
      return;
    }

    setIsFetchingArtlist(true);
    setFetchError(null);

    try {
      const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/artlist/${artlistId}`, { injectToken: true });

      if (response.ok) {
        if (Array.isArray(data?.artlist_artworks) && data.artlist_artworks.length > 0) {
          navigate(`/artlist/${encodeURIComponent(data?._id ?? artlistId)}`, {
            state: { openTimeSelector: true },
          });
        } else {
          setFetchError("This artlist does not have artworks yet.");
        }
      } else {
        setFetchError("Unable to open this artlist right now.");
      }
    } catch (error) {
      console.error("Failed to load artlist", error);
      setFetchError("Unable to open this artlist right now.");
    } finally {
      setIsFetchingArtlist(false);
    }
  }

  return (
    <div className="artlist-slider-card__container">
      <div className="artlist-slider-card__image-wrapper">
        {imageId ? (
          <ArtworkImage
            image={imageId}
            imageClassName="artlist-slider-card__image"
            imageTemplateClassName="artlist-slider-card__image"
            style={{ aspectRatio: "1 / 1" }}
          />
        ) : (
          <div className="artlist-slider-card__no-image">No image</div>
        )}
      </div>
      <div className="artlist-slider-card__info">
        <Text variant="h3" className="artlist-slider-card__title">{title}</Text>
        <Text variant="body2" className="artlist-slider-card__author">by {author}</Text>
        <PlayIcon
          className={`artlist-slider-card__play-icon${isFetchingArtlist ? " artlist-slider-card__play-icon--loading" : ""}`}
          onClick={handlePlayClick}
        />
        {fetchError && (
          <span className="artlist-slider-card__play-error">{fetchError}</span>
        )}
      </div>
    </div>
  );
}
